import { useEffect, useMemo, useState } from 'react'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import { PageTitle } from '@components'
import { UseData } from '@hooks'
import { formatMinutesDuration } from '@utils'
import './data.style.scss'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
    },
  },
}

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(46, 127, 123, 0.12)',
      },
      ticks: {
        stepSize: 1,
        precision: 0,
      },
      border: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
    },
  },
  barPercentage: 0.72,
  categoryPercentage: 0.58,
}

const buildPomodoroChartData = stats => ({
  labels: ['Concluídos', 'Não concluídos'],
  datasets: [
    {
      data: [stats.pomodorosCompleted, stats.pomodorosIncomplete],
      backgroundColor: ['#2e7f7b', '#f29166'],
      borderWidth: 0,
      hoverOffset: 6,
    },
  ],
})

const buildTaskChartData = stats => ({
  labels: ['Realizadas', 'Pendentes'],
  datasets: [
    {
      data: [stats.tasksCompleted, stats.tasksPending],
      backgroundColor: ['#2e7f7b', '#f29166'],
      borderRadius: 10,
      borderSkipped: false,
    },
  ],
})

const renderStatCard = ({ label, value, tone = 'default' }) => (
  <article
    className={`relatorio__stat-card relatorio__stat-card--${tone}`}
    key={label}
  >
    <span className="relatorio__stat-card__label">{label}</span>
    <strong className="relatorio__stat-card__value">{value}</strong>
  </article>
)

function RelatorioScreen() {
  const [relatorio, setRelatorio] = useState()

  const { getData } = UseData()

  useEffect(() => {
    const syncRelatorio = () => {
      const resultado = getData()

      if (resultado) {
        setRelatorio(resultado)
      }
    }

    syncRelatorio()

    const intervalId = window.setInterval(syncRelatorio, 15000)
    window.addEventListener('focus', syncRelatorio)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', syncRelatorio)
    }
  }, [getData])

  const resumo = useMemo(() => {
    if (!relatorio) {
      return null
    }

    return {
      totalPomodoros:
        relatorio.pomodorosCompleted + relatorio.pomodorosIncomplete,
      totalTarefas: relatorio.tasksCompleted + relatorio.tasksPending,
      tempoTotalFoco: formatMinutesDuration(relatorio.totalFocusTime),
    }
  }, [relatorio])

  if (!relatorio || !resumo) {
    return <div>Carregando relatório...</div>
  }

  const pomodoroStats = [
    {
      label: 'Tempo total de foco',
      value: resumo.tempoTotalFoco,
      tone: 'blue',
    },
    {
      label: 'Pomodoros concluídos',
      value: relatorio.pomodorosCompleted,
      tone: 'green',
    },
    {
      label: 'Pomodoros não concluídos',
      value: relatorio.pomodorosIncomplete,
      tone: 'orange',
    },
    {
      label: 'Pomodoros registrados',
      value: resumo.totalPomodoros,
      tone: 'neutral',
    },
  ]

  const tarefaStats = [
    {
      label: 'Tarefas realizadas',
      value: relatorio.tasksCompleted,
      tone: 'green',
    },
    {
      label: 'Tarefas pendentes',
      value: relatorio.tasksPending,
      tone: 'orange',
    },
    {
      label: 'Tarefas na lista',
      value: resumo.totalTarefas,
      tone: 'neutral',
    },
  ]

  return (
    <section className="relatorio">
      <div className="relatorio__header">
        <PageTitle titulo="Relatório de Produtividade" />
      </div>

      <div className="relatorio__grid">
        <article className="relatorio__panel">
          <header className="relatorio__panel__header">
            <h2 className="relatorio__panel__title">Pomodoros</h2>
          </header>

          <div className="relatorio__panel__body">
            <div className="relatorio__panel__chart-shell">
              <div className="relatorio__panel__chart-holder">
                <Doughnut
                  data={buildPomodoroChartData(relatorio)}
                  options={doughnutOptions}
                />
              </div>
            </div>

            <div className="relatorio__stats-grid relatorio__stats-grid--pomodoro">
              {pomodoroStats.map(renderStatCard)}
            </div>
          </div>
        </article>

        <article className="relatorio__panel">
          <header className="relatorio__panel__header">
            <h2 className="relatorio__panel__title">Tarefas</h2>
          </header>

          <div className="relatorio__panel__body">
            <div className="relatorio__panel__chart-shell relatorio__panel__chart-shell--bar">
              <div className="relatorio__panel__chart-holder relatorio__panel__chart-holder--bar">
                <Bar
                  data={buildTaskChartData(relatorio)}
                  options={barOptions}
                />
              </div>
            </div>

            <div className="relatorio__stats-grid relatorio__stats-grid--tarefas">
              {tarefaStats.map(renderStatCard)}
            </div>
          </div>
        </article>
      </div>
    </section>
  )
}

export { RelatorioScreen }
